/**
 * MONSTER TOWN — THE PLACEABLE REGISTRY. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * LAW #1 — EVERYTHING YOU TAP DOES SOMETHING — IS ENFORCED BY THE COMPILER HERE.
 *
 *   - `interact` is a REQUIRED field. You cannot register a prop without one.
 *   - `interact` must RETURN an `InteractionResult`.
 *   - `InteractionResult.feedback` is `NonEmpty<InteractionFeedback>`, i.e.
 *     `readonly [F, ...F[]]`. `feedback: []` IS A TYPE ERROR. There is no
 *     `kind: 'none'` feedback — the type system gives you no way to say
 *     "nothing happens".
 *   - `npm run audit` additionally runs every handler in a sandbox and flags
 *     anything that throws, returns only a sound, or never touches state/art.
 *
 * CONTENT LIVES IN DATA. A new furniture set, room type or venue must be a
 * `definePlaceable()` drop in `src/content/**` — never an engine change. If you
 * find yourself editing engine code to add a prop, you have made a mistake.
 */

import type * as THREE from 'three'
import type { Cell, Footprint, Grid, Placement, Rot } from './grid'
import type { PaletteName } from './palette'
import type {
  InstanceId,
  NonEmpty,
  PlaceableId,
  PlacedInstance,
  SceneId,
  Vec3,
} from './types'

// ---------------------------------------------------------------------------
// taxonomy
// ---------------------------------------------------------------------------

/** Shop tabs and build-palette groups are driven off this. Additive only. */
export type PlaceableCategory =
  | 'block' // plain building blocks — FREE FOREVER
  | 'floor' // floor tiles — FREE FOREVER
  | 'wall' // wall pieces — FREE FOREVER
  | 'roof'
  | 'door'
  | 'window'
  | 'stair'
  | 'path' // paths & roads — FREE FOREVER
  | 'fence'
  | 'furniture' // seating, tables, storage
  | 'bed'
  | 'kitchen'
  | 'bath'
  | 'light'
  | 'decor' // posters, rugs, plants-in-pots, silly stuff
  | 'plant' // flowers, bushes
  | 'tree'
  | 'water'
  | 'townProp' // benches, lamp posts, mailboxes, signs
  | 'venueFixture' // counters, order stations, display cases
  | 'toy'
  | 'appliance'

/** Which grid scale a piece is authored for. `both` = usable in either. */
export type PlaceableScale = 'exterior' | 'interior' | 'both'

// ---------------------------------------------------------------------------
// art
// ---------------------------------------------------------------------------

/** Everything an art builder is allowed to know. Deterministic in, mesh out. */
export interface ArtContext {
  readonly def: AnyPlaceableDef
  /** null while drawing a build-mode ghost or a shop thumbnail. */
  readonly instance: PlacedInstance | null
  readonly grid: Grid
  readonly anchor: Cell
  readonly rot: Rot
  /** 4-bit N/E/S/W auto-connect mask. 0 when the def declares no connect group. */
  readonly connect: number
  /** 8-bit mask (orthogonals + NE/SE/SW/NW) for inner-corner art. */
  readonly connect8: number
  /** Kid-chosen recolour, or null. */
  readonly tint: PaletteName | null
  /** Stable 0..1 per-instance value for harmless variety (leaf rotation etc). */
  readonly variety: number
  /** true when building a ghost preview: skip particles, keep it cheap. */
  readonly ghost: boolean
}

/**
 * Build the art for one placement.
 *
 * CONTRACT:
 *  - Return a Group whose ORIGIN is the footprint centre at floor level; the
 *    caller positions it with `grid.footprintCenter()` and rotates by `rot`.
 *    Do NOT bake position or rotation into the mesh.
 *  - Must be deterministic for the same ArtContext — it is rebuilt whenever a
 *    neighbour changes.
 *  - Must be cheap: reuse shared geometry/material from `src/render/kit.ts`.
 *    Older-iPad budget is real.
 *  - Name any sub-object you want to animate (`obj.name = 'lid'`) so the
 *    interaction handler can find it via `ctx.art.getObjectByName('lid')`.
 */
export type ArtBuilder = (ctx: ArtContext) => THREE.Object3D

// ---------------------------------------------------------------------------
// interaction  (LAW #1)
// ---------------------------------------------------------------------------

/**
 * The observable consequences of a tap. At least one is required by the type
 * system. There is deliberately no "nothing happened" variant.
 */
export type InteractionFeedback =
  /** The prop itself animates. `clip` is handled by the owning crew's art. */
  | { kind: 'anim'; clip: string; seconds?: number; target?: string }
  /** The player's monster does something (sit, sleep, eat, bounce, dance). */
  | { kind: 'monster'; action: string; seconds?: number }
  /** A visual on/off or variant change baked into the prop's art. */
  | { kind: 'toggle'; on: boolean; target?: string }
  /** Particles / sparkles / bubbles / steam. */
  | { kind: 'particles'; effect: string; at?: Vec3; count?: number }
  /** Instance state changed and must be saved. */
  | { kind: 'state'; patch: Record<string, unknown> }
  /** Coins fly out. Positive only — nothing in this game takes coins on a tap. */
  | { kind: 'coins'; amount: number; source: string }
  /** Something is created in the world (a plate of food, a bubble, a flower). */
  | { kind: 'spawn'; what: string; at?: Vec3 }
  /** Open a picture panel. Never text-only. LAW #4. */
  | { kind: 'ui'; panel: string }
  /** Walk through a door into another scene. */
  | { kind: 'scene'; to: SceneId; spawn?: Cell }
  /** Web Audio cue id. */
  | { kind: 'sound'; cue: string; volume?: number }
  /** Recorded voice line key (namespaced, e.g. 'mt.bed.goodnight'). */
  | { kind: 'voice'; key: string }

/** Feedback kinds that a kid can SEE. Sound/voice alone is flagged by the audit. */
export const VISIBLE_FEEDBACK: ReadonlySet<InteractionFeedback['kind']> = new Set([
  'anim',
  'monster',
  'toggle',
  'particles',
  'state',
  'coins',
  'spawn',
  'ui',
  'scene',
])

export interface InteractionResult {
  /** MUST have at least one entry. `[]` will not compile. */
  readonly feedback: NonEmpty<InteractionFeedback>
  /**
   * Where the monster should stand to do this. Defaults to the nearest walkable
   * cell adjacent to the footprint.
   */
  readonly standAt?: Vec3
  /** Cooldown before the same prop reacts again. 0 = instant re-tap (default). */
  readonly cooldownMs?: number
}

/** Everything an interaction handler is allowed to know. Keep it small. */
export interface InteractionContext<S = Record<string, unknown>> {
  readonly def: AnyPlaceableDef
  readonly instance: PlacedInstance
  readonly placement: Placement
  readonly sceneId: SceneId
  readonly grid: Grid
  /** Live per-instance state. Mutate it AND report a `state` feedback. */
  readonly state: S
  /** The built art group for this instance — find named parts to animate. */
  readonly art: THREE.Object3D | null
  /** World position of the prop's centre. */
  readonly at: Vec3
  /** How many times this instance has been tapped, this session. */
  readonly taps: number
  /** ms since app start. Deterministic under the harness. */
  readonly now: number
  /** Seeded random, 0..1. Never use Math.random in a handler. */
  readonly rng: () => number
}

/** REQUIRED on every placeable. Returning nothing is a compile error. */
export type InteractionHandler<S = Record<string, unknown>> = (
  ctx: InteractionContext<S>,
) => InteractionResult

// ---------------------------------------------------------------------------
// the definition
// ---------------------------------------------------------------------------

export interface PlaceableDef<S extends Record<string, unknown> = Record<string, unknown>> {
  readonly id: PlaceableId
  /** Dev-facing only. Never shown to a kid — LAW #4, icons and voice only. */
  readonly devName: string
  readonly category: PlaceableCategory
  readonly scale: PlaceableScale
  readonly footprint: Footprint
  /** Coins. 0 = FREE FOREVER (blocks, paths, walls, floors, plain furniture). */
  readonly cost: number
  /** Unlock tier id required before it appears in the shop. undefined = day one. */
  readonly tier?: string
  /** Can other pieces stack on top of it? */
  readonly stackable: boolean
  /** Can a monster walk over it? Floors/paths/rugs: true. Furniture: false. */
  readonly walkable: boolean
  /** Auto-connect group (LAW #5). Pieces with the same group join up. */
  readonly connect?: { readonly group: string; readonly style: 'wall' | 'path' | 'fence' | 'edge' }
  /** Recolour options offered to the kid. Empty = not recolourable. */
  readonly tints?: readonly PaletteName[]
  /** Free-form tags for shop filtering and NPC reactions ('cosy', 'silly'). */
  readonly tags?: readonly string[]

  readonly buildArt: ArtBuilder
  /** Optional flat icon override. Default: an auto-rendered 3D thumbnail. */
  readonly icon2d?: (c: CanvasRenderingContext2D, size: number) => void

  /** LAW #1. Required. Must return at least one feedback. */
  readonly interact: InteractionHandler<S>
  readonly initialState?: () => S
}

export type AnyPlaceableDef = PlaceableDef<Record<string, unknown>>

/**
 * Author a placeable. Use this instead of a bare object literal — it gives you
 * inference on the state type inside `interact`.
 */
export function definePlaceable<S extends Record<string, unknown> = Record<string, unknown>>(
  def: PlaceableDef<S>,
): PlaceableDef<S> {
  return def
}

// ---------------------------------------------------------------------------
// registry
// ---------------------------------------------------------------------------

const REGISTRY = new Map<PlaceableId, AnyPlaceableDef>()

export function registerPlaceable(def: PlaceableDef<never> | AnyPlaceableDef): void {
  const d = def as AnyPlaceableDef
  if (REGISTRY.has(d.id)) {
    throw new Error(`placeable id "${d.id}" registered twice`)
  }
  REGISTRY.set(d.id, d)
}

export function registerPlaceables(defs: readonly unknown[]): void {
  for (const d of defs) registerPlaceable(d as AnyPlaceableDef)
}

export function getPlaceable(id: PlaceableId): AnyPlaceableDef | undefined {
  return REGISTRY.get(id)
}

/** Throws with a clear message — a missing def is a content bug, not a runtime state. */
export function requirePlaceable(id: PlaceableId): AnyPlaceableDef {
  const d = REGISTRY.get(id)
  if (d === undefined) throw new Error(`unknown placeable "${id}"`)
  return d
}

export function allPlaceables(): AnyPlaceableDef[] {
  return [...REGISTRY.values()]
}

export function placeablesByCategory(cat: PlaceableCategory): AnyPlaceableDef[] {
  return allPlaceables().filter((d) => d.category === cat)
}

export function placeablesForScale(scale: 'exterior' | 'interior'): AnyPlaceableDef[] {
  return allPlaceables().filter((d) => d.scale === scale || d.scale === 'both')
}

/** TEST ONLY. Never call from game code. */
export function __clearRegistry(): void {
  REGISTRY.clear()
}

// ---------------------------------------------------------------------------
// LAW #1 AUDIT
// ---------------------------------------------------------------------------

export interface AuditIssue {
  id: PlaceableId
  devName: string
  severity: 'error' | 'warn'
  code:
    | 'threw'
    | 'soundOnly'
    | 'emptyFeedback'
    | 'duplicateResult'
    | 'noStateOnRepeat'
    | 'missingArt'
  message: string
}

export interface AuditReport {
  total: number
  passed: number
  issues: AuditIssue[]
  ok: boolean
}

function auditContextFor(def: AnyPlaceableDef, taps: number, grid: Grid): InteractionContext {
  let seed = 1337
  const rng = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0
    return seed / 0x100000000
  }
  const anchor: Cell = { gx: 0, gy: 0, gz: 0 }
  const instance: PlacedInstance = {
    id: '__audit' as InstanceId,
    defId: def.id,
    sceneId: '__audit' as SceneId,
    anchor,
    rot: 0,
    state: def.initialState?.() ?? {},
  }
  const placement: Placement = {
    instanceId: instance.id,
    defId: def.id,
    anchor,
    rot: 0,
    footprint: def.footprint,
    connectGroup: def.connect?.group ?? null,
    stackable: def.stackable,
    walkable: def.walkable,
  }
  return {
    def,
    instance,
    placement,
    sceneId: instance.sceneId,
    grid,
    state: instance.state ?? {},
    art: null,
    at: { x: 0, y: 0, z: 0 },
    taps,
    now: 1000 * taps,
    rng,
  }
}

/**
 * Run every registered placeable's interaction twice in a sandbox and report
 * anything that isn't a real reaction. `npm run audit` drives this through the
 * live registry in the browser so content drops can never sneak a dead prop in.
 */
export function auditPlaceables(grid: Grid): AuditReport {
  const issues: AuditIssue[] = []
  const defs = allPlaceables()

  for (const def of defs) {
    const base = { id: def.id, devName: def.devName }
    let first: InteractionResult
    try {
      first = def.interact(auditContextFor(def, 0, grid))
    } catch (err) {
      issues.push({
        ...base,
        severity: 'error',
        code: 'threw',
        message: `interact() threw: ${String(err)}`,
      })
      continue
    }

    if (!Array.isArray(first.feedback) || first.feedback.length === 0) {
      issues.push({
        ...base,
        severity: 'error',
        code: 'emptyFeedback',
        message: 'interact() returned no feedback — this prop is dead to a kid',
      })
      continue
    }

    const visible = first.feedback.some((f) => VISIBLE_FEEDBACK.has(f.kind))
    if (!visible) {
      issues.push({
        ...base,
        severity: 'error',
        code: 'soundOnly',
        message: 'only sound/voice feedback — nothing a kid can SEE happens',
      })
    }

    // Tapping again should not be literally identical forever for props that
    // claim state. This is a warning, not a hard failure: a lamp that toggles
    // is fine, a stove that plays the same 1-frame blip forever is not.
    try {
      const second = def.interact(auditContextFor(def, 3, grid))
      const a = JSON.stringify(first.feedback)
      const b = JSON.stringify(second.feedback)
      const hasState = first.feedback.some((f) => f.kind === 'state' || f.kind === 'toggle')
      if (a === b && !hasState && first.feedback.length === 1) {
        issues.push({
          ...base,
          severity: 'warn',
          code: 'duplicateResult',
          message: 'single, identical reaction on every tap — consider variation',
        })
      }
    } catch (err) {
      issues.push({
        ...base,
        severity: 'error',
        code: 'threw',
        message: `interact() threw on repeat tap: ${String(err)}`,
      })
    }
  }

  const errors = issues.filter((i) => i.severity === 'error').length
  return {
    total: defs.length,
    passed: defs.length - errors,
    issues,
    ok: errors === 0,
  }
}
